'use client';

/**
 * Session — the single source of truth for "who is signed in".
 *
 * Replaces client-side JWT decoding. The credential is an HttpOnly cookie in deployed
 * environments (JS cannot read it), so the browser asks the server via /api/me instead.
 * On localhost the Bearer header is sent instead — same endpoint, same answer, one code
 * path for both.
 *
 * status:
 *   'loading'         — /api/me in flight. Guards MUST NOT redirect yet, or every page
 *                       refresh bounces the user to login.
 *   'authenticated'   — user + tenant available.
 *   'unauthenticated' — no valid credential. The server said so (401/403).
 *   'error'           — we could not find out. A 5xx, a network failure or a timeout is
 *                       NOT proof the user is signed out; treating it as such bounces a
 *                       signed-in user to a login screen at random. Guards should offer a
 *                       retry instead.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { getAuthToken } from '../utils/authToken';
import { httpFetch } from '@/lib/httpClient';
import { logAccess, reportError } from '@/observability/clientLogger';

export interface SessionUser {
  id: string | null;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  role?: string;
  roleId?: string | null;
}

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

// A hung request never rejects, so without this the app spins on the loading state.
const ME_TIMEOUT_MS = 15 * 1000;

interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  tenant: string | null;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [tenant, setTenant] = useState<string | null>(null);

  // 'ok' carries the server's answer. 'unauthenticated' is the server's considered
  // rejection. 'error' means we could not reach a verdict and should try again.
  type MeResult =
    | { kind: 'ok'; body: { success?: boolean; user?: SessionUser; tenant?: string } }
    | { kind: 'unauthenticated' }
    | { kind: 'error' };

  const fetchMe = useCallback(async (): Promise<MeResult> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ME_TIMEOUT_MS);

    try {
      // On localhost getAuthToken() returns the localStorage token → Bearer header.
      // Deployed it returns null → the HttpOnly cookie authenticates (credentials are
      // attached globally by installCredentialedFetch).
      const token = getAuthToken();

      const response = await httpFetch('/api/me', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: controller.signal,
      });

      // Only the auth-specific rejections mean "signed out". Anything else (5xx, gateway,
      // proxy error) is an outage, not a logout.
      if (response.status === 401 || response.status === 403) return { kind: 'unauthenticated' };
      if (!response.ok) {
        // Distinct from the 401 path above: this is an outage being reported as a
        // session problem, and the user sees the same screen either way.
        reportError(new Error(`/api/me returned ${response.status}`), {
          event_detail: 'session_check_bad_status', status: response.status,
        });
        return { kind: 'error' };
      }

      return { kind: 'ok', body: await response.json() };
    } catch (error) {
      // AbortError included — a timeout is transient. Still reported: this catch
      // returns a fallback the caller cannot tell apart from a real answer, and
      // it is the one that fires when /api/me never reached a server at all.
      reportError(error, { event_detail: 'session_check_failed' });
      return { kind: 'error' };
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const refresh = useCallback(async () => {
    let result = await fetchMe();

    // One retry absorbs the common cold-start blip before we surface anything to the user.
    if (result.kind === 'error') {
      await new Promise(resolve => setTimeout(resolve, 600));
      result = await fetchMe();
    }

    if (result.kind === 'error') {
      // Leave user/tenant untouched — a previously resolved session is still the best
      // information we have, and discarding it would log the user out on a network blip.
      // If we were already authenticated (this was a background re-check triggered by a
      // storage event), stay that way rather than interrupting a working session.
      setStatus(prev => (prev === 'authenticated' ? 'authenticated' : 'error'));
      return;
    }

    if (result.kind === 'unauthenticated') {
      setStatus('unauthenticated');
      setUser(null);
      setTenant(null);
      return;
    }

    const body = result.body;
    setUser(body.user ?? null);
    setTenant(body.tenant ?? null);
    setStatus(body.success && body.tenant ? 'authenticated' : 'unauthenticated');

    // The session resolving is what unblocks every guard and every App below it.
    // Logged once per resolution, not per render: a session that never resolves and
    // one that resolved as signed-out look identical on screen.
    logAccess('shell_session_resolved', {
      authenticated: !!(body.success && body.tenant),
      hasTenant: !!body.tenant,
    });
  }, [fetchMe]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ status, user, tenant, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
